import type { UserContextDto } from '@mfw/common';

import {
  isRealCivilDay,
  PasswordDto,
  toCivilDate,
  UsernameDto,
  UserGender,
} from '@mfw/common';

import { z } from 'zod';

import type {
  MaybeBoolean,
  MaybeString,
  MaybeNumber,
  Maybe,
} from '../types/maybe.types';
import type { ApiMembershipTermsOfUse } from './termsOfUse.schema';
import type {
  ApiAddressRecord as ApiAddress,
  UiAddressRecord as UiAddress,
} from './address.schema';
import type {
  ApiContactRecord as ApiContact,
  UiContact,
} from './contacts.schema';
import type { UiOrganizationUser } from './organizationUser/organization.user.schema';
import type { ApiOrganization, UiOrganization } from './organizations.schema';
import type { ApiTranslation, UiTranslation } from './translations.schema';
import type { ApiSocialMedia, UiSocialMedia } from './socialMedia.schema';
import type { ApiCorpOrganization } from './corpOrganizations.schema';
import type { ApiPermission, UiPermission } from './permissions';
import type { ApiTenant } from './tenants.schema';
import type { ApiStatus } from './status.schema';

import { UiSocialMediaSchema } from './socialMedia.schema';
import { type ApiTag } from './tags.schema';
import config from '../config';

export type ApiUserSettings = {
  gender: Maybe<UserGender>;
  privacyPolicyAccepted: MaybeBoolean;
  bodyMassIndex: Maybe<'kg' | 'psf'>;
  height: Maybe<'cm' | 'ft' | 'mm'>;
  energy: Maybe<'kcal' | 'kj'>;
  length: Maybe<'km' | 'mi'>;
  weight: Maybe<'kg' | 'lb' | 'oz'>;
  volume: Maybe<'l' | 'oz'>;
  dateOfBirth: MaybeString;
  rememberMe: MaybeBoolean;
  profileUrl: MaybeString;
  isPublic: MaybeBoolean;
  /** The account's EXPLICIT choice — only the Personal Details form writes it. */
  timezone: MaybeString;
  /**
   * The zone the browser last reported, refreshed by the BE on every
   * `GET /auth/me`. Read-only here: it is what the server falls back to when
   * `timezone` is unset, so a surface that shows "the account's timezone" has
   * to show this when there is no explicit choice — otherwise it renders empty
   * while the whole scheduling stack is happily using this value.
   */
  detectedTimezone: MaybeString;
  locale: MaybeString;
  // Filled by the mapper only, never sent to the API
  ownLocale?: MaybeString;
  /** Daily Activity — 'HH:mm', null = that reminder is off (the default). */
  dailyActivityReminderMorningTime: MaybeString;
  dailyActivityReminderEveningTime: MaybeString;
  id: string;
};

export type ApiUserFitnessProfile = {
  healthIssues: Maybe<Partial<ApiTag>[]>;
  fitnessGoals: Maybe<Partial<ApiTag>[]>;
  muscleMassPercentage: MaybeNumber;
  bodyFatPercentage: MaybeNumber;
  fitnessLevel: MaybeNumber;
  maxHeartRate: MaybeNumber;
  muscleMass: MaybeNumber;
  bodyFat: MaybeNumber;
  height: MaybeNumber;
  weight: MaybeNumber;
  heart: MaybeNumber;
  bmi: MaybeNumber;
  id: string;
};

export type UiUserFitnessProfile = {
  fitnessGoals: Maybe<
    { translations: ApiTranslation[]; checked: boolean; value: string }[]
  >;
  healthIssues: Maybe<{ checked: boolean; value: string }[]>;
} & Omit<ApiUserFitnessProfile, 'fitnessGoals' | 'healthIssues'>;

export type ApiUserTrainer = {
  settings: Maybe<Pick<ApiUserSettings, 'profileUrl'>>;
  name: MaybeString;
  id: string;
};

export type ApiUser = {
  roles: Maybe<Array<{ translations: Array<ApiTranslation>; id: string }>>;
  fitnessProfiles: Maybe<ApiUserFitnessProfile[]>;
  organizationUsers: Maybe<UiOrganizationUser[]>;
  corpOrganization: Maybe<ApiCorpOrganization>;
  socialMedia: Maybe<Array<ApiSocialMedia>>;
  // F3 — profile switcher context persistence.
  lastUsedContext?: Maybe<UserContextDto>;
  onboardingCompletedAt?: MaybeString;
  trainers: Maybe<ApiUserTrainer[]>;
  settings: Maybe<ApiUserSettings>;
  corpOrganizationId: MaybeString;
  addresses: Maybe<ApiAddress[]>;
  contacts: Maybe<ApiContact[]>;
  isPlatformUser: MaybeBoolean;
  status: Maybe<ApiStatus>;
  profileUrl: MaybeString;
  createdAt: MaybeString;
  email: MaybeString;
  name: MaybeString;
  username?: MaybeString;
  id: MaybeString;
};

// The wire shape `GET /auth/check-invite` actually answers — only what
// `requireLiveInviteToken` has cleared before any credential write, never
// the broad `ApiUser` `mapUserFromApi` builds.
export type ApiInviteCheck = Pick<ApiUser, 'id' | 'name' | 'status'> & {
  organizationId: string | null;
  organizationName: string | null;
  // Present only when this invite carries a RECURRING membership — the
  // acceptor must consent to its Terms of Use before `accept-invite`
  // succeeds (see `AcceptInvitePage`'s pre-submit Terms of Use step). The
  // full snapshot travels with `check-invite` (not just an id) because this
  // runs before the visitor has any session to fetch it separately with.
  pendingRecurringMembership: {
    userMembershipId: string;
    termsOfUse: ApiMembershipTermsOfUse;
  } | null;
  // 6.10 — the organization's own terms, present only when it asks clients to
  // accept them on connecting. Same snapshot-with-the-check reasoning.
  connectionTermsOfUse: ApiMembershipTermsOfUse | null;
};

export type ApiAuthor = {
  roles: Maybe<Array<{ translations: Array<ApiTranslation>; id: string }>>;
  status: Maybe<Extract<ApiStatus, 'ACTIVE' | 'ARCHIVED' | 'PENDING'>>;
  fitnessProfiles: Maybe<ApiUserFitnessProfile[]>;
  personalTrainerOrganizationId: MaybeString;
  socialMedia: Maybe<Array<ApiSocialMedia>>;
  organizations: Maybe<ApiOrganization[]>;
  settings: Maybe<ApiUserSettings>;
  addresses: Maybe<ApiAddress[]>;
  contacts: Maybe<ApiContact[]>;
  isPlatformUser: MaybeBoolean;
  isPersonalTrainer: boolean;
  profileUrl: MaybeString;
  createdAt: MaybeString;
  email: MaybeString;
  name: MaybeString;
  id: MaybeString;
};

export type ApiAuthUser = {
  // F8 fix: the real BE values (SecurityGuard) are OrganizationUserType
  // ('CLIENT'/'TRAINER'/'STAFF'/'OWNER') or 'PLATFORM' — 'ORGANIZATION' was
  // never an actual value (this field is the caller's org-role type, not
  roles: Maybe<Array<{ translations: Array<UiTranslation>; id: string }>>;
  // Organization.type) and 'OWNER' was missing.
  type: Maybe<'CLIENT' | 'OWNER' | 'PLATFORM' | 'STAFF' | 'TRAINER'>;
  lastUsedOrganization: Maybe<ApiOrganization>;
  owningOrganization: MaybeBoolean;
  mustChangePassword: MaybeBoolean;
  isPersonalTrainer: MaybeBoolean;
  permissions: ApiPermission[];
  isPlatformUser: MaybeBoolean;
  // F1b (platform-user-class): after the P5 bypass both platform roles hold
  // every permission, so anything super-admin-only gates on this flag.
  isSuperAdmin: MaybeBoolean;
  // 10.1 — the server switch for card billing of client memberships
  membershipCardBillingEnabled: MaybeBoolean;
  tenant: Maybe<ApiTenant>;
  isPremium: MaybeBoolean;
  tenantId: MaybeString;
  user: Maybe<ApiUser>;
};

export type UiAuthUser = {
  // settings.dateOfBirth stays a civil 'yyyy-MM-dd' string end-to-end; the
  // JS Date exists only inside FormCivilDatePicker.
  settings: Maybe<ApiUserSettings>;
  roles: Maybe<Array<{ translations: Array<UiTranslation>; id: string }>>;
  fitnessProfile: Maybe<UiUserFitnessProfile>;
  lastUsedOrganization: Maybe<UiOrganization>;
  personalTrainerOrganizationId: MaybeString;
  hasFinishedOnboarding: MaybeBoolean;
  permissions: Maybe<UiPermission[]>;
  address: Maybe<UiAddress>;
  contact: Maybe<UiContact>;
  id: string;
} & Omit<
  ApiUser,
  | 'addresses'
  | 'contacts'
  | 'dateOfBirth'
  | 'fitnessProfiles'
  | 'lastUsedOrganization'
  | 'roles'
  | 'settings'
> &
  Omit<ApiAuthUser, 'lastUsedOrganization' | 'permissions' | 'user'>;

export type UiUser = Omit<
  UiAuthUser,
  | 'corpOrganization'
  | 'corpOrganizationId'
  | 'hasFinishedOnboarding'
  | 'isPersonalTrainer'
  | 'isPlatformUser'
  | 'isSuperAdmin'
  | 'isPremium'
  | 'lastUsedOrganization'
  | 'membershipCardBillingEnabled'
  | 'mustChangePassword'
  | 'owningOrganization'
  | 'permissions'
  | 'tenant'
  | 'tenantId'
  | 'type'
>;

export type UiAuthorUser = Omit<
  UiAuthUser,
  | 'corpOrganization'
  | 'corpOrganizationId'
  | 'hasFinishedOnboarding'
  | 'isPlatformUser'
  | 'isSuperAdmin'
  | 'isPremium'
  | 'lastUsedOrganization'
  | 'membershipCardBillingEnabled'
  | 'mustChangePassword'
  | 'owningOrganization'
  | 'permissions'
  | 'tenant'
  | 'tenantId'
  | 'type'
> & {
  personalTrainerOrganizationId: MaybeString;
  isPersonalTrainer: boolean;
};

export type ApiUserUpdate = {
  address?: Partial<{
    country: MaybeString;
    street: MaybeString;
    city: MaybeString;
    zip: MaybeString;
  }>;
  contact?: {
    phoneCountryCode?: MaybeString;
    phoneNumber?: MaybeString;
  };
  fitnessProfile?: Partial<ApiUserFitnessProfile>;
  socialMedia?: Maybe<Array<UiSocialMedia>>;
  userSettings?: Partial<ApiUserSettings>;
  id?: MaybeString;
};

export const EmailSchema = z
  .string()
  .trim()
  .email('VALIDATION__InvalidEmail')
  .min(3, 'VALIDATION__Min3CharLimit')
  .max(255, 'VALIDATION__Max255CharLimit');

export const UiUserTimezoneStepSchema = z.object({
  locale: z
    .array(
      z.object({
        label: z.string().or(z.literal('')).optional(),
        value: z.string().min(1, 'VALIDATION__Required'),
      }),
    )
    .nonempty(),
  timezone: z
    .array(
      z.object({
        label: z.string().or(z.literal('')).optional(),
        value: z.string().min(1, 'VALIDATION__Required'),
      }),
    )
    .nonempty(),
});
export type UiUserTimezoneStep = z.infer<typeof UiUserTimezoneStepSchema>;

export const UiUserUpdateSchema = z
  .object({
    username: UsernameDto.optional(),
    address: z
      .object({
        city: z.string().or(z.literal('')).nullable().optional(),
        country: z
          .array(z.object({ value: z.string() }))
          .nonempty({ message: 'VALIDATION__Required' }),
        isAddressPublic: z.boolean().nullable().optional(),
        street: z.string().or(z.literal('')).nullable().optional(),
        zip: z.string().or(z.literal('')).nullable().optional(),
      })
      .partial()
      .optional(),
    contact: z
      .object({
        isPhoneNumberPublic: z.boolean().nullable().optional(),
        phoneCountryCode: z.string().or(z.literal('')),
        phoneNumber: z.string().or(z.literal('')),
      })
      .optional(),
    email: EmailSchema.optional(),
    fitnessProfile: z
      .object({
        id: z.string().or(z.literal('')),
        bmi: z.number().or(z.literal(0)).optional().nullable(),
        bodyFat: z.number().or(z.literal(0)).optional().nullable(),
        bodyFatPercentage: z.number().or(z.literal(0)).optional().nullable(),
        fitnessGoals: z
          .array(
            z.object({ checked: z.boolean(), value: z.string() }).optional(),
          )
          .optional(),
        fitnessLevel: z.string().or(z.string()).or(z.literal('1')).optional(),
        healthIssues: z
          .array(
            z.object({ checked: z.boolean(), value: z.string() }).optional(),
          )
          .optional(),
        heart: z.number().or(z.literal(0)).optional().nullable(),
        height: z.number().or(z.literal(0)).optional().nullable(),
        maxHeartRate: z.number().or(z.literal(0)).optional().nullable(),
        muscleMass: z.number().or(z.literal(0)).optional().nullable(),
        muscleMassPercentage: z.number().or(z.literal(0)).optional().nullable(),
        weight: z.number().or(z.literal(0)).optional().nullable(),
      })
      .partial()
      .optional(),
    name: z
      .string()
      .min(3, 'VALIDATION__Min3CharLimit')
      .max(255, 'VALIDATION__Max255CharLimit')
      .optional(),
    socialMedia: z.array(UiSocialMediaSchema).min(0).optional(),
    userSettings: z
      .object({
        id: z.string().or(z.literal('')),
        bodyMassIndex: z.array(
          z.object({
            value: z.enum(['kg', 'psf']),
          }),
        ),
        dateOfBirth: z
          // invalid_type_error as well as required_error: FormCivilDatePicker
          // stores `null` when the field is cleared, and zod calls that an
          // invalid type, not a missing value — without this the user reads a
          // raw, untranslated "Expected string, received null".
          .string({
            invalid_type_error: 'VALIDATION__Required',
            required_error: 'VALIDATION__Required',
          })
          .refine(isRealCivilDay, 'VALIDATION__Required')
          .refine(
            (day) =>
              day <= toCivilDate(config.onboarding.miniumDateOfBirthDate),
            'VALIDATION__Min16Age',
          ),
        energy: z
          .array(
            z.object({
              value: z.enum(['kcal', 'kj']),
            }),
          )
          .optional(),
        gender: z
          .array(
            z.object({
              value: z.nativeEnum(UserGender),
            }),
          )
          .optional(),
        height: z
          .array(
            z.object({
              value: z.enum(['cm', 'mm', 'ft']),
            }),
          )
          .optional(),
        isPublic: z.boolean().or(z.null()).optional(),
        length: z
          .array(
            z.object({
              value: z.enum(['km', 'mi']),
            }),
          )
          .optional(),
        locale: UiUserTimezoneStepSchema.shape.locale.optional(),
        privacyPolicyAccepted: z.boolean(),
        profileUrl: z.string().or(z.null()),
        rememberMe: z.boolean(),
        timezone: UiUserTimezoneStepSchema.shape.timezone.optional(),
        volume: z
          .array(
            z.object({
              value: z.enum(['l', 'oz']),
            }),
          )
          .optional(),
        weight: z
          .array(
            z.object({
              value: z.enum(['kg', 'lb', 'oz']),
            }),
          )
          .optional(),
        // Daily Activity — plain 'HH:mm' (not a select, so no [{value}]
        // wrapping like the fields above); null clears/disables the reminder.
        dailyActivityReminderMorningTime: z
          .string()
          .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'VALIDATION__InvalidTime')
          .nullable()
          .optional(),
        dailyActivityReminderEveningTime: z
          .string()
          .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'VALIDATION__InvalidTime')
          .nullable()
          .optional(),
      })
      .partial()
      .optional(),
  })
  .partial();

export type UiUserUpdate = z.infer<typeof UiUserUpdateSchema>;

export const UiUserAddressStepSchema = z.object({
  address: z.object({
    city: z.string().or(z.literal('')).nullable().optional(),
    country: z
      .array(z.object({ value: z.string() }))
      .nonempty({ message: 'VALIDATION__Required' }),
    isAddressPublic: z.boolean().nullable().optional(),
    street: z.string().or(z.literal('')).nullable().optional(),
    zip: z.string().or(z.literal('')).nullable().optional(),
  }),
  contact: z.object({
    isPhoneNumberPublic: z.boolean().nullable().optional(),
    phoneCountryCode: z.string().min(1, 'VALIDATION__Required'),
    phoneNumber: z.string().min(1, 'VALIDATION__Required'),
  }),
  email: EmailSchema,
  name: z
    .string()
    .min(3, 'VALIDATION__Min3CharLimit')
    .max(255, 'VALIDATION__Max255CharLimit'),
});

export type UiUserAddressStep = z.infer<typeof UiUserAddressStepSchema>;

export const UiUserAboutStepSchema = z.object({
  dateOfBirth: z
    // See the userSettings.dateOfBirth note above: a cleared
    // FormCivilDatePicker stores `null`, which is an invalid type to zod.
    .string({
      invalid_type_error: 'VALIDATION__Required',
      required_error: 'VALIDATION__Required',
    })
    .refine(isRealCivilDay, 'VALIDATION__Required')
    .refine(
      (day) => day <= toCivilDate(config.onboarding.miniumDateOfBirthDate),
      'VALIDATION__Min16Age',
    ),
  // 7.3 — chosen, never pre-filled: it decides the fitness score rows
  gender: z
    .array(
      z.object({
        label: z.string().or(z.literal('')),
        value: z.nativeEnum(UserGender),
      }),
    )
    .min(1, 'VALIDATION__Required'),
  height: z.number({
    invalid_type_error: 'VALIDATION__Required',
    required_error: 'VALIDATION__Required',
  }),
  weight: z.number({
    invalid_type_error: 'VALIDATION__Required',
    required_error: 'VALIDATION__Required',
  }),
});

export type UiUserAboutStep = z.infer<typeof UiUserAboutStepSchema>;

export const UiUserGoalsStepSchema = z.object({
  fitnessGoals: z.array(
    z.object({ checked: z.boolean(), value: z.string() }).optional(),
  ),
});

export type UiUserGoalsStep = z.infer<typeof UiUserGoalsStepSchema>;

export const UiUserHealthIssuesStepSchema = z.object({
  healthIssues: z.array(
    z.object({ checked: z.boolean(), value: z.string() }).optional(),
  ),
});

export type UiUserHealthIssuesStep = z.infer<
  typeof UiUserHealthIssuesStepSchema
>;

export const UiUserTrainingLevelStepSchema = z.object({
  fitnessLevel: z.string().min(1, 'VALIDATION__Required'),
});

export type UiUserTrainingLevelStep = z.infer<
  typeof UiUserTrainingLevelStepSchema
>;

export type UiUserCreateOnboarding = UiUserTimezoneStep & UiUserAddressStep;

export const AcceptInviteDto = z
  .object({
    privacyPolicy: z
      .boolean({ message: 'VALIDATION__Required' })
      .refine((value) => value === true, {
        message: 'VALIDATION__Required',
        path: [],
      }),
  })
  .strict();

export const UiUserInviteSchema = z.object({
  email: z.string().trim().email({ message: 'VALIDATION__InvalidEmail' }),
  membershipId: z.array(z.string()).optional(),
  // Same shape as AssignMembershipDialog's own fields — only meaningful
  // alongside membershipId; SINGLE/false is every pre-existing invite's
  // implicit behavior.
  type: z.enum(['SINGLE', 'RECURRING']).optional(),
  requiresCardLinkNow: z.boolean().optional(),
  autoRenew: z.boolean().optional(),
  name: z.string().optional().or(z.literal('')),
  questionnaireIds: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
  roleIds: z.array(z.object({ value: z.string() })),
  trainerId: z
    .array(z.object({ label: z.string(), value: z.string() }))
    .optional(),
});

export type UiUserInviteArgs = z.infer<typeof UiUserInviteSchema>;

export const RegisterUserSchema = z
  .object({
    confirmPassword: PasswordDto,
    email: z.string().trim().email({ message: 'VALIDATION__InvalidEmail' }),
    name: z
      .string()
      .min(3, { message: 'VALIDATION__Min3CharLimit' })
      .max(255, { message: 'VALIDATION__Max255CharLimit' }),
    username: UsernameDto,
    password: PasswordDto,
    privacyPolicy: z
      .boolean({
        message: 'VALIDATION__Required',
        required_error: 'VALIDATION__Required',
      })
      .refine(
        (value) => {
          return value === true;
        },
        { message: 'VALIDATION__Required', path: [] },
      ),
  })
  .strict()
  .refine(
    ({ confirmPassword, password }) => {
      return confirmPassword === password;
    },
    {
      message: 'VALIDATION__PasswordsMustMatch',
      path: ['confirmPassword'],
    },
  );

export const CorpPasswordChangeSchema = z
  .object({
    confirmPassword: z.string(),
    newPassword: PasswordDto,
  })
  .strict()
  .refine(
    ({ confirmPassword, newPassword }) => confirmPassword === newPassword,
    {
      message: 'VALIDATION__PasswordsMustMatch',
      path: ['confirmPassword'],
    },
  );

export const ChangePasswordSchema = z
  .object({
    confirmPassword: z.string(),
    currentPassword: z.string().min(1, { message: 'VALIDATION__Required' }),
    newPassword: PasswordDto,
  })
  .strict()
  .refine(
    ({ confirmPassword, newPassword }) => confirmPassword === newPassword,
    {
      message: 'VALIDATION__PasswordsMustMatch',
      path: ['confirmPassword'],
    },
  );

export type ChangePasswordFormData = z.infer<typeof ChangePasswordSchema>;

export const LoginUserSchema = z
  .object({
    email: z.string().trim().email({ message: 'VALIDATION__InvalidEmail' }),
    password: z
      .string()
      .min(3, { message: 'VALIDATION__Min3CharLimit' })
      .max(255, { message: 'VALIDATION__Max255CharLimit' }),
  })
  .strict();

export const ForgotPasswordUserSchmea = z
  .object({
    email: z.string().trim().email({ message: 'VALIDATION__InvalidEmail' }),
  })
  .strict();

export const ResetPasswordUserSchema = z
  .object({
    password: PasswordDto,
  })
  .strict();
export type ResetPasswordFormData = z.infer<typeof ResetPasswordUserSchema>;
